import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authAxios } from "../utils/auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Settings as SettingsIcon, Users, Activity, Zap, FileCode, Database, UserCircle, Menu, X, Brain, Headset, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import UserManagement from "@/components/admin/UserManagement";
import AdminSettings from "@/components/admin/AdminSettings";
import LLMMonitor from "@/components/admin/LLMMonitor";
import TokenManagement from "@/components/admin/TokenManagement";
import SystemPrompts from "@/components/admin/SystemPrompts";
import V3Artifacts from "@/components/admin/V3Artifacts";
import PersonaSettings from "@/components/admin/PersonaSettings";
import EmailTemplates from "@/components/admin/EmailTemplates";
import SessionDigests from "@/components/admin/SessionDigests";
import AdminMenu from "../components/AdminMenu";

export default function AdminPanel() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Get tab from URL params, default to "users"
  const activeTab = searchParams.get("tab") || "users";
  
  const tabItems = [
    { value: "users", label: "Users", icon: Users },
    { value: "persona", label: "Persona", icon: UserCircle },
    { value: "tokens", label: "Tokens", icon: Zap },
    { value: "prompts", label: "Prompts", icon: FileCode },
    { value: "v3-artifacts", label: "V3 Memory", icon: Database },
    { value: "llm-monitor", label: "LLM Monitor", icon: Activity },
    { value: "digests", label: "Digests", icon: Sparkles },
    { value: "settings", label: "Settings", icon: SettingsIcon },
    { value: "email-templates", label: "Email", icon: Mail },
  ];
  
  const handleTabChange = (value) => {
    // Update URL params when tab changes
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", value);
    // Preserve agent param if switching to prompts tab
    if (value !== "prompts") {
      newParams.delete("agent");
    }
    setSearchParams(newParams, { replace: true });
    setMobileMenuOpen(false); // Close mobile menu on tab change
  };

  const checkAdminAccess = async () => {
    try {
      const response = await authAxios.get("/auth/me");
      const userData = response.data;
      
      if (!["admin", "support"].includes(userData.role)) {
        toast.error("You don't have permission to access the admin panel");
        navigate("/dashboard");
        return;
      }
      
      setUser(userData);
      setLoading(false);
    } catch (error) {
      console.error("Admin access check failed:", error);
      navigate("/dashboard");
    }
  };

  useEffect(() => {
    checkAdminAccess();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-divine-black flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-divine-gold border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-divine-black text-white">
      {/* Mobile Header */}
      <div className="md:hidden bg-divine-charcoal border-b border-divine-gold/20 sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/dashboard")}
              className="text-divine-gold hover:bg-divine-gold/10 h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2">
              <Brain className="w-6 h-6 text-divine-gold" />
              <span className="text-lg font-cinzel text-divine-gold">Admin</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
              <AdminMenu />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white hover:bg-white/10"
                data-testid="admin-mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
        </div>
        
        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-divine-charcoal border-b border-divine-gold/20 shadow-lg z-50">
            {/* User Info */}
            <div className="px-4 py-3 border-b border-white/10">
              <p className="text-sm text-white">{user?.name}</p>
              <p className="text-xs text-gray-400">{user?.email}</p>
              <p className="text-xs text-divine-gold capitalize mt-1">Role: {user?.role}</p>
            </div>
            {/* Tab Items */}
            <div className="py-2">
              {tabItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.value}
                    onClick={() => handleTabChange(item.value)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      activeTab === item.value
                        ? "bg-divine-gold/20 text-divine-gold"
                        : "text-white/70 hover:bg-white/5 hover:text-white"
                    }`}
                    data-testid={`admin-mobile-tab-${item.value}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm">{item.label}</span>
                  </button>
                );
              })}
              {/* Help Desk Link */}
              <button
                onClick={() => navigate("/helpdesk")}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-purple-400 hover:bg-purple-500/10 transition-colors border-t border-white/10 mt-2"
                data-testid="admin-mobile-helpdesk"
              >
                <Headset className="h-4 w-4" />
                <span className="text-sm">Help Desk</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Desktop Header */}
      <div className="hidden md:block bg-divine-charcoal border-b border-divine-gold/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="text-divine-gold hover:bg-divine-gold/10"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-cinzel text-divine-gold">Admin Panel</h1>
                <p className="text-sm text-gray-400 mt-1">
                  Role: <span className="text-divine-gold capitalize">{user?.role}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/helpdesk")}
                className="bg-purple-500 hover:bg-purple-600 text-white"
                data-testid="admin-helpdesk-btn"
              >
                <Headset className="h-4 w-4 mr-2" />
                Help Desk
              </Button>
              <div className="text-right">
                <p className="text-sm text-gray-400">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <AdminMenu />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="hidden md:grid w-full max-w-5xl grid-cols-9 bg-divine-charcoal border border-divine-gold/20">
            <TabsTrigger 
              value="users"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
              data-testid="admin-tab-users"
            >
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger 
              value="persona"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
              data-testid="admin-tab-persona"
            >
              <UserCircle className="h-4 w-4 mr-2" />
              Persona
            </TabsTrigger>
            <TabsTrigger 
              value="tokens"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
              data-testid="admin-tab-tokens"
            >
              <Zap className="h-4 w-4 mr-2" />
              Tokens
            </TabsTrigger>
            <TabsTrigger 
              value="prompts"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
              data-testid="admin-tab-prompts"
            >
              <FileCode className="h-4 w-4 mr-2" />
              Prompts
            </TabsTrigger>
            <TabsTrigger 
              value="v3-artifacts"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
              data-testid="admin-tab-v3"
            >
              <Database className="h-4 w-4 mr-2" />
              V3 Memory
            </TabsTrigger>
            <TabsTrigger 
              value="llm-monitor"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
              data-testid="admin-tab-llm"
            >
              <Activity className="h-4 w-4 mr-2" />
              LLM Monitor
            </TabsTrigger>
            <TabsTrigger
              value="digests"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
              data-testid="admin-tab-digests"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Digests
            </TabsTrigger>
            <TabsTrigger 
              value="settings"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
              data-testid="admin-tab-settings"
            >
              <SettingsIcon className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger 
              value="email-templates"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
              data-testid="admin-tab-email"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-6">
            <UserManagement />
          </TabsContent>

          <TabsContent value="persona" className="mt-6">
            <PersonaSettings />
          </TabsContent>

          <TabsContent value="tokens" className="mt-6">
            <TokenManagement />
          </TabsContent>

          <TabsContent value="prompts" className="mt-6">
            <SystemPrompts />
          </TabsContent>

          <TabsContent value="v3-artifacts" className="mt-6">
            <V3Artifacts />
          </TabsContent>

          <TabsContent value="llm-monitor" className="mt-6">
            <LLMMonitor />
          </TabsContent>

          <TabsContent value="digests" className="mt-6">
            <SessionDigests />
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <AdminSettings />
          </TabsContent>

          <TabsContent value="email-templates" className="mt-6">
            <EmailTemplates />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
